import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useTheme } from '../contexts/ThemeContext';
import { useOmnichannel } from '../contexts/OmnichannelContext';
import ConversationList from '../components/omnichannel/ConversationList';
import ConversationDetail from '../components/omnichannel/ConversationDetail';
import ConversationFilters from '../components/omnichannel/ConversationFilters';
import CustomerSidebar from '../components/omnichannel/CustomerSidebar';
import { Conversation, ConversationFilters as Filters } from '../services/omnichannelService';
import { useOmnichannelWebSocket } from '../hooks/useOmnichannelWebSocket';
import omnichannelService from '../services/omnichannelService';
import { t } from 'i18next';

export default function OmnichannelPage() {
  const { theme } = useTheme();
  const location = useLocation();
  const { refreshCounts } = useOmnichannel();
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [filters, setFilters] = useState<Filters>({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Handle deep linking to conversation
  useEffect(() => {
    if (location.state?.selectedConversationId) {
      omnichannelService.getConversation(location.state.selectedConversationId)
        .then(conv => setSelectedConversation(conv))
        .catch(console.error);
    }
  }, [location.state]);

  // Set up WebSocket listener for real-time updates
  useOmnichannelWebSocket({
    onNewMessage: () => {
      refreshCounts();
    },
    onConversationUpdated: () => {
      refreshCounts();
    }
  });

  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
  };

  const handleConversationUpdate = useCallback(async () => {
    console.log('🔄 Manually refreshing conversation list...');
    setRefreshKey((prev) => prev + 1);
    refreshCounts();

    // Refresh the selected conversation to get updated data (including tags)
    if (selectedConversation) {
      try {
        const updatedConversation = await omnichannelService.getConversation(selectedConversation.id);
        setSelectedConversation(updatedConversation);
      } catch (error) {
        console.error('Failed to refresh selected conversation:', error);
      }
    }
  }, [refreshCounts, selectedConversation]);

  return (
    <DashboardLayout>
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div
          className={`flex-shrink-0 border-b px-6 py-4 ${theme === 'dark'
              ? 'border-gray-700 bg-gray-800'
              : 'border-gray-200 bg-white'
            }`}
        >
          <div className="flex items-center gap-3">
            <MessageSquare className="h-6 w-6 text-blue-500" />
            <h1
              className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}
            >
              {t('atrium.conversations')}
            </h1>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex min-h-0">
          {/* Left Sidebar - Conversation List */}
          <div
            className={`w-96 border-r flex flex-col ${theme === 'dark'
                ? 'border-gray-700 bg-gray-800'
                : 'border-gray-200 bg-white'
              }`}
          >
            <ConversationFilters filters={filters} onFiltersChange={setFilters} />
            <div className="flex-1 overflow-hidden">
              <ConversationList
                key={refreshKey}
                selectedId={selectedConversation?.id}
                onSelect={handleSelectConversation}
                filters={filters}
              />
            </div>
          </div>

          {/* Center - Conversation Detail */}
          <div className="flex-1 flex">
            {selectedConversation ? (
              <>
                <div className="flex-1">
                  <ConversationDetail
                    conversationId={selectedConversation.id}
                    onUpdate={handleConversationUpdate}
                    isSidebarCollapsed={isSidebarCollapsed}
                    onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  />
                </div>

                {/* Right Sidebar - Customer Info */}
                {!isSidebarCollapsed && (
                  <CustomerSidebar
                    conversation={selectedConversation}
                    onUpdate={handleConversationUpdate}
                  />
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare
                    className={`h-16 w-16 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                      }`}
                  />
                  <p
                    className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}
                  >
                    {t('atrium.selectConversation')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
